import toast from "react-hot-toast";
import Swal from "./swalCompat";

/**
 * One place for every user-facing notice in the admin.
 *
 * Replaces the blocking browser primitives (`alert`, `confirm`, `prompt`)
 * that were still scattered through the game-settings, mail and profile
 * pages. Those froze the page, ignored the theme, and on some browsers were
 * silently suppressed after a couple of fires — none of which is acceptable
 * for a save confirmation.
 *
 * Usage:
 *   notify.success("Settings saved");
 *   notify.error(err, "Could not save settings");   // unwraps axios errors
 *   if (await notify.confirm({ title, text })) { ... }
 *   const reason = await notify.prompt({ title, label });
 */

const messageFrom = (input, fallback) => {
  if (typeof input === "string" && input.trim()) return input;
  if (input && typeof input === "object") {
    return (
      input?.response?.data?.message ||
      input?.data?.message ||
      input?.message ||
      fallback
    );
  }
  return fallback;
};

const notify = {
  success: (message, title) =>
    Swal.fire({ icon: "success", title: title || "Success", text: messageFrom(message, "Done") }),

  error: (error, fallback = "Something went wrong") =>
    Swal.fire({ icon: "error", title: "Error", text: messageFrom(error, fallback) }),

  warning: (message, title = "Heads up") =>
    Swal.fire({ icon: "warning", title, text: messageFrom(message, "") }),

  info: (message, title) =>
    Swal.fire({ icon: "info", title: title || "", text: messageFrom(message, "") }),

  /** Resolves to a boolean instead of SweetAlert's result object. */
  confirm: async (options = {}) => {
    const result = await Swal.fire({
      icon: "question",
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      ...options,
      showCancelButton: true,
    });
    return Boolean(result?.isConfirmed);
  },

  /** Resolves to the typed string, or undefined if dismissed. */
  prompt: async (options = {}) => {
    const result = await Swal.fire({
      input: options.multiline === false ? "text" : "textarea",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      ...options,
    });
    return result?.isConfirmed ? result.value : undefined;
  },

  /** Ties a toast to a promise: loading → success/error automatically. */
  promise: (promise, { loading = "Working…", success = "Done", error = "Failed" } = {}) =>
    toast.promise(promise, { loading, success, error }),

  dismiss: () => toast.dismiss(),
};

export default notify;
